import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { Duration } from "date-fns";
import { useCallback } from "react";
import { ActiveFreezeMap, freezeQueryCache } from "./freezeQueryCache";
import {
  ChainIdLegacy,
  SupportedChainId,
  SupportedChainValue,
} from "@silo/types";

type PostMutationUpdate = (params: {
  queryKey: QueryKey;
  value: unknown;
  chainId: SupportedChainId;
}) => void;

const CACHE_FREEZE_DURATION: SupportedChainValue<Duration | null> = {
  [ChainIdLegacy.MAINNET]: null,
  [ChainIdLegacy.LOCAL]: null,
  [ChainIdLegacy.ARBITRUM]: { seconds: 30 },
  [ChainIdLegacy.OPTIMISM]: null,
  [ChainIdLegacy.BASE]: null,
  [ChainIdLegacy.ANVIL]: null,
};

const activeFreezeMap: ActiveFreezeMap = new Map();

export const usePostMutationUpdate = () => {
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache();

  const postMutationUpdate: PostMutationUpdate = useCallback(
    ({ queryKey, value, chainId }) => {
      const duration = CACHE_FREEZE_DURATION[chainId];

      /**
       * For some L2 chains like Arbitrum, there is a minor lag between when a
       * transaction is mined, and when the updated blockchain state can be
       * read. To account for this, we freeze the cache value for a period of
       * time, so that any background fetches do not show the outdated
       * blockchain state.
       */
      if (duration) {
        freezeQueryCache({
          window,
          queryKey,
          value,
          queryClient,
          queryCache,
          activeFreezeMap,
          duration,
        });
      } else {
        queryClient.setQueryData(queryKey, value);
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient, queryCache]
  );

  return postMutationUpdate;
};
